// Gestiona las secciones/módulos usados para clasificar los formularios:
// crear, renombrar y eliminar. Al eliminar una sección, los formularios que
// la usaban quedan sin sección (no se borran).
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, Check, Loader, AlertCircle, FolderKanban } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";
import { api } from "../api";

export default function FormModulesModal({ onClose, onChanged }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [error, setError] = useState(null);
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => {
    setLoading(true);
    api.getFormModules().then(data => {
      setModules(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const notifyChanged = () => onChanged && onChanged();

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    const result = await api.createFormModule(name);
    setCreating(false);
    if (result.error) { setError(result.error); return; }
    setNewName("");
    load();
    notifyChanged();
  };

  const startEdit = (module) => {
    setEditingId(module.id);
    setEditingName(module.name);
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name) return;
    const result = await api.updateFormModule(editingId, name);
    if (result.error) { setError(result.error); return; }
    setEditingId(null);
    load();
    notifyChanged();
  };

  const handleDelete = async (module) => {
    const ok = await confirm({
      title: "Eliminar sección",
      message: `"${module.name}" será eliminada. Los formularios que la usan quedarán sin sección.`,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await api.deleteFormModule(module.id);
    load();
    notifyChanged();
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FDF2F8", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FolderKanban size={18} style={{ color: "#DB2777" }} />
            </div>
            <h2>Secciones de formularios</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-form" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="form-input"
              style={{ flex: 1 }}
              value={newName}
              placeholder="Nombre de la nueva sección (ej: Ingreso)"
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
            />
            <button className="btn-secondary" onClick={handleCreate} disabled={!newName.trim() || creating}>
              {creating ? <Loader size={14} className="spin" /> : <Plus size={14} />} Agregar
            </button>
          </div>

          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem", marginTop: 10 }}>
              <AlertCircle size={15} /> {error}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                <Loader size={24} className="spin" opacity={0.4} />
              </div>
            ) : modules.length === 0 ? (
              <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", textAlign: "center", padding: "20px 0" }}>
                No hay secciones creadas aún.
              </p>
            ) : modules.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
                {editingId === m.id ? (
                  <>
                    <input
                      className="form-input"
                      style={{ flex: 1 }}
                      value={editingName}
                      autoFocus
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && saveEdit()}
                    />
                    <button className="btn-icon" onClick={saveEdit} title="Guardar"><Check size={15} /></button>
                    <button className="btn-icon" onClick={() => setEditingId(null)} title="Cancelar"><X size={15} /></button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: "0.86rem", cursor: "pointer" }} onClick={() => startEdit(m)}>{m.name}</span>
                    <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>
                      {m.form_count} formulario{m.form_count !== 1 ? "s" : ""}
                    </span>
                    <button className="btn-icon" onClick={() => handleDelete(m)} title="Eliminar"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </motion.div>
      {ConfirmUI}
    </motion.div>
  );
}
